import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  CategoriesListResponse,
  CategoryType,
  CategoriesFilterParams,
  CategoriesTreeResponse,
  CreateCategoryParams,
  UpdateCategoryParams
} from '@/types/category'
import { BulkIdParams, BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum, SortOrderEnum } from '@/enums'

// API functions
import {
  fetchCategories,
  fetchCategoriesTree,
  fetchCategory,
  createCategory,
  updateCategory,
  deleteCategory,
  sortCategory,
  bulkDeleteCategories
} from '@/lib/api'

type CategoriesListStateType = {
  data: CategoriesListResponse | null
  isLoading: boolean
  error: string | null
}

type CategoriesTreeStateType = {
  data: CategoriesTreeResponse | null
  isLoading: boolean
  error: string | null
}

type CategoryStateType = {
  data: CategoryType | null
  isLoading: boolean
  error: {
    status?: number
    message: string
  } | null
}

type CategoriesStoreState = {
  // Filters state
  filters: CategoriesFilterParams
  setFilters: (filters: Partial<CategoriesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  categoriesList: CategoriesListStateType
  categoriesTree: CategoriesTreeStateType
  categoryState: CategoryStateType
  selectedCategoryIds: string[]

  // API functions
  fetchCategories: (params?: CategoriesFilterParams) => Promise<CategoriesListResponse>
  resetCategoriesList: () => void
  fetchCategoriesTree: () => Promise<CategoriesTreeResponse>
  resetCategoriesTree: () => void

  // Single category operations
  getCategory: (categoryId: string) => Promise<CategoryType>
  createCategory: (params: CreateCategoryParams) => Promise<CategoryType>
  updateCategory: (categoryId: string, params: UpdateCategoryParams) => Promise<CategoryType>
  sortCategory: (categoryId: string, type: SortOrderEnum) => Promise<CategoryType>
  deleteCategory: (categoryId: string) => Promise<void>
  bulkDeleteCategories: (params: BulkIdParams) => Promise<BulkDeleteResponse>
  resetCategory: () => void

  // Selection operations
  selectCategory: (id: string) => void
  deselectCategory: (id: string) => void
  toggleCategorySelection: (id: string) => void
  selectAllCategories: (categories: CategoryType[]) => void
  deselectAllCategories: () => void
  isCategorySelected: (id: string) => boolean
}

const defaultFilters: CategoriesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'left'
}

const initialCategoryState: CategoryStateType = {
  data: null,
  isLoading: false,
  error: null
}

const useCategoriesStore = create<CategoriesStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    categoriesList: {
      data: null,
      isLoading: false,
      error: null
    },
    categoriesTree: {
      data: null,
      isLoading: false,
      error: null
    },
    categoryState: initialCategoryState,
    selectedCategoryIds: [],

    setFilters: newFilters =>
      set(state => ({
        filters: { ...state.filters, ...newFilters }
      })),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: CategoriesFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as SortEnum
            break
          case 'sortBy':
            filters[key] = value
            break
          case 'q':
          case 'createdAtStart':
          case 'createdAtEnd':
          case 'updatedAtStart':
          case 'updatedAtEnd':
          case 'active': // <-- add this line
            filters[key] = value
            break
        }
      })

      set(state => ({
        filters: { ...state.filters, ...filters }
      }))
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString())
        }
      })

      return params
    },

    fetchCategories: async (params = {}) => {
      set(state => ({
        categoriesList: {
          ...state.categoriesList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const finalParams = { ...get().filters, ...params }
        const data = await fetchCategories(finalParams)

        set(state => ({
          categoriesList: {
            ...state.categoriesList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          categoriesList: {
            ...state.categoriesList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetCategoriesList: () => {
      set(state => ({
        categoriesList: {
          ...state.categoriesList,
          data: null,
          error: null
        }
      }))
    },

    fetchCategoriesTree: async () => {
      set(state => ({
        categoriesTree: {
          ...state.categoriesTree,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchCategoriesTree()

        set(state => ({
          categoriesTree: {
            ...state.categoriesTree,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          categoriesTree: {
            ...state.categoriesTree,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetCategoriesTree: () => {
      set(state => ({
        categoriesTree: {
          ...state.categoriesTree,
          data: null,
          error: null
        }
      }))
    },

    // Single category operations
    getCategory: async (categoryId: string) => {
      set(() => ({
        categoryState: {
          ...initialCategoryState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchCategory(categoryId)

        set(() => ({
          categoryState: {
            ...initialCategoryState,
            data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          categoryState: {
            ...initialCategoryState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    createCategory: async (params: CreateCategoryParams) => {
      set(() => ({
        categoryState: {
          ...initialCategoryState,
          isLoading: true
        }
      }))

      try {
        const data = await createCategory(params)

        set(() => ({
          categoryState: {
            ...initialCategoryState,
            data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          categoryState: {
            ...initialCategoryState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        // Handle validation errors (422)
        if (error.response?.status === 422) {
          const validationError = new Error('Validation failed')
          ;(validationError as any).response = error.response
          ;(validationError as any).isValidationError = true
          throw validationError
        }

        throw error
      }
    },

    updateCategory: async (categoryId: string, params: UpdateCategoryParams) => {
      set(() => ({
        categoryState: {
          ...get().categoryState,
          isLoading: true
        }
      }))

      try {
        const data = await updateCategory(categoryId, params)

        set(() => ({
          categoryState: {
            ...get().categoryState,
            isLoading: false,
            data,
            error: null
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          categoryState: {
            ...get().categoryState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        // Handle validation errors (422)
        if (error.response?.status === 422) {
          const validationError = new Error('Validation failed')
          ;(validationError as any).response = error.response
          ;(validationError as any).isValidationError = true
          throw validationError
        }

        throw error
      }
    },

    sortCategory: async (categoryId: string, type: SortOrderEnum) => {
      set(() => ({
        categoryState: {
          ...get().categoryState,
          isLoading: true
        }
      }))

      try {
        const data = await sortCategory(categoryId, type)

        set(() => ({
          categoryState: {
            ...get().categoryState,
            isLoading: false,
            data,
            error: null
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          categoryState: {
            ...get().categoryState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        throw error
      }
    },

    deleteCategory: async (categoryId: string) => {
      try {
        await deleteCategory(categoryId)
        return
      } catch (error) {
        throw error
      }
    },

    bulkDeleteCategories: async (params: BulkIdParams) => {
      try {
        return await bulkDeleteCategories(params)
      } catch (error) {
        throw error
      }
    },

    resetCategory: () => {
      set(() => ({
        categoryState: initialCategoryState
      }))
    },

    // Selection operations
    selectCategory: id => {
      set(state => {
        if (state.selectedCategoryIds.includes(id)) {
          return state
        }
        return { selectedCategoryIds: [...state.selectedCategoryIds, id] }
      })
    },

    deselectCategory: id => {
      set(state => ({
        selectedCategoryIds: state.selectedCategoryIds.filter(categoryId => categoryId !== id)
      }))
    },

    toggleCategorySelection: id => {
      set(state => {
        if (state.selectedCategoryIds.includes(id)) {
          return { selectedCategoryIds: state.selectedCategoryIds.filter(categoryId => categoryId !== id) }
        }
        return { selectedCategoryIds: [...state.selectedCategoryIds, id] }
      })
    },

    selectAllCategories: categories => {
      set(() => ({
        selectedCategoryIds: categories.map(category => category.id)
      }))
    },

    deselectAllCategories: () => {
      set(() => ({ selectedCategoryIds: [] }))
    },

    isCategorySelected: id => {
      return get().selectedCategoryIds.includes(id)
    }
  }))
)

export default useCategoriesStore
